/**
 * Change Password Page
 * 
 * Used for:
 * 1. Forced password change after first login with temporary password
 * 2. Voluntary password change from settings
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Shield } from 'lucide-react';
import { getFriendlyError } from '../utils/friendlyErrors';

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const { login, apiCall, token } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const isRequired = searchParams.get('required') === 'true';
  
  // Get temp credentials if this is a forced change
  const tempToken = sessionStorage.getItem('temp_token');
  const tempUserStr = sessionStorage.getItem('temp_user');
  const tempUser = tempUserStr ? JSON.parse(tempUserStr) : null;
  
  // Password validation
  const passwordChecks = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    match: newPassword === confirmPassword && newPassword.length > 0,
    different: newPassword !== currentPassword && newPassword.length > 0
  };
  
  const isValidPassword = Object.values(passwordChecks).every(Boolean);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidPassword) {
      toast({
        title: 'Invalid Password',
        description: 'Please ensure all password requirements are met.',
        variant: 'destructive'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      const authToken = tempToken || token;
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      if (response.ok) {
        setSuccess(true);
        
        // If this was a forced change, now complete the login
        if (isRequired && tempToken && tempUser) {
          // Clear temp storage
          sessionStorage.removeItem('temp_token');
          sessionStorage.removeItem('temp_user');
          
          // Complete the login
          login(tempUser, tempToken);
          
          toast({
            title: '✅ Password Changed Successfully',
            description: 'You can now use your new password to log in.',
          });
          
          // Redirect to dashboard after a brief delay
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        } else {
          toast({
            title: '✅ Password Changed',
            description: 'Your password has been updated successfully.',
          });
          
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
        }
      } else {
        const error = await response.json();
        toast({
          title: 'Password Change Failed',
          description: error.detail || 'Failed to change password. Please try again.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Unable to change password',
        description: getFriendlyError(error, 'Something went wrong. Please try again.'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // If success, show success message
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Password Changed!</h2>
            <p className="text-gray-500">Redirecting to dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
          <CardTitle>{isRequired ? 'Set Your New Password' : 'Change Password'}</CardTitle>
          <CardDescription>
            {isRequired 
              ? 'Your account requires a password change. Please set a new secure password.'
              : 'Enter your current password and choose a new one.'
            }
          </CardDescription>
          {isRequired && tempUser && (
            <p className="text-sm text-blue-600 mt-2">
              Logged in as: {tempUser.email}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Current/Temporary Password */}
            <div className="space-y-2">
              <Label>{isRequired ? 'Temporary Password' : 'Current Password'}</Label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder={isRequired ? 'Enter temporary password' : 'Enter current password'}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* New Password */}
            <div className="space-y-2">
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* Confirm Password */}
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </div>
            
            {/* Password Requirements */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium text-gray-700">Password Requirements:</p>
              <div className="grid grid-cols-2 gap-2">
                <PasswordCheck checked={passwordChecks.length} text="At least 8 characters" />
                <PasswordCheck checked={passwordChecks.uppercase} text="One uppercase letter" />
                <PasswordCheck checked={passwordChecks.lowercase} text="One lowercase letter" />
                <PasswordCheck checked={passwordChecks.number} text="One number" />
                <PasswordCheck checked={passwordChecks.match} text="Passwords match" />
                <PasswordCheck checked={passwordChecks.different} text="Different from current" />
              </div>
            </div>
            
            <Button type="submit" className="w-full" disabled={!isValidPassword || loading}>
              {loading ? 'Changing Password...' : 'Change Password'}
            </Button>
            
            {!isRequired && (
              <Button 
                type="button" 
                variant="outline" 
                className="w-full"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Password check indicator component
const PasswordCheck = ({ checked, text }) => (
  <div className={`flex items-center gap-1.5 text-xs ${checked ? 'text-green-600' : 'text-gray-400'}`}>
    {checked ? (
      <CheckCircle className="h-3.5 w-3.5" />
    ) : (
      <div className="h-3.5 w-3.5 rounded-full border border-gray-300" />
    )}
    {text}
  </div>
);

export default ChangePassword;
